import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiDelete, apiGet, apiPost, operationsClient } from '@/lib/api';
import { noteEndpoints } from '@/lib/api/endpoints';
import {
  normalizeAttachmentFile,
  validateAttachmentFile,
} from '@/lib/attachments/attachment-files';
import { noteKeys } from '@/lib/queryKeys';
import type { NoteAttachment, NoteEntityType } from '@/types/note';

/**
 * Files hang off a note, and the note list serves them inline (with presigned
 * URLs) — so there is no per-note read here, only the write paths, which
 * refresh the note list they belong to.
 */

export async function getEntityNoteAttachmentDownloadUrl(noteId: string, attachmentId: string) {
  const raw = await apiGet<Record<string, unknown>>(
    operationsClient,
    noteEndpoints.attachmentDownloadUrl(noteId, attachmentId),
  );
  return String(raw.downloadUrl ?? raw.url ?? '');
}

/**
 * Opens a note's file with a URL signed at click time. The URL embedded in a
 * note response is only suitable for the inline thumbnail: a long-lived job
 * tab can keep that response after its one-hour S3 signature has expired.
 */
export async function openNoteAttachment(noteId: string, attachment: NoteAttachment) {
  // Open synchronously from the click gesture, then resolve and navigate it.
  // Calling window.open only after the request is treated as an unsolicited
  // popup by browsers and nothing appears.
  const target = window.open('about:blank', '_blank');
  if (!target) throw new Error('The browser blocked the attachment tab');
  target.opener = null;
  try {
    const url = await getEntityNoteAttachmentDownloadUrl(noteId, attachment.id);
    if (!url) throw new Error('Attachment download URL is unavailable');
    if (target.closed) throw new Error('The attachment tab was closed');
    target.location.replace(url);
  } catch (error) {
    target.close();
    throw error;
  }
}

export async function uploadEntityNoteAttachment(noteId: string, file: File) {
  const error = validateAttachmentFile(file);
  if (error) throw new Error(error);

  const raw = await apiPost<Record<string, unknown>>(
    operationsClient,
    noteEndpoints.attachmentUploadUrl(noteId),
    { fileName: file.name, contentType: file.type, fileSize: file.size },
  );

  const uploadUrl = String(raw.uploadUrl ?? '');
  const attachment = normalizeAttachmentFile((raw.attachment ?? {}) as Record<string, unknown>);

  const response = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  });
  if (!response.ok) throw new Error(`Upload failed (${response.status})`);

  return attachment;
}

export function useNoteAttachmentActions(
  entityType: NoteEntityType,
  entityId: string,
  noteId?: string | null,
) {
  const queryClient = useQueryClient();

  async function invalidate() {
    await queryClient.invalidateQueries({ queryKey: noteKeys.entity(entityType, entityId) });
  }

  async function uploadFiles(files: File[]) {
    if (!noteId || files.length === 0) return;
    for (const file of files) {
      await uploadEntityNoteAttachment(noteId, file);
    }
    await invalidate();
    toast.success(files.length === 1 ? 'File attached.' : 'Files attached.');
  }

  async function deleteAttachment(attachmentId: string) {
    if (!noteId) return;
    await apiDelete<{ deleted: boolean }>(
      operationsClient,
      noteEndpoints.attachmentDelete(noteId, attachmentId),
    );
    await invalidate();
    toast.success('File deleted.');
  }

  return { uploadFiles, deleteAttachment, invalidate };
}
